import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Settings, PlusCircle, Coins, X } from 'lucide-react';
import { format, parseISO, subHours } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export default function MobileNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentDate = new Date();
  const [showHiddenForm, setShowHiddenForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Determine the correct date based on the time
      const submitDate = new Date();
      const actualDate = submitDate.getHours() < 7
        ? new Date(submitDate.getFullYear(), submitDate.getMonth(), submitDate.getDate() - 1)
        : submitDate;

      const numericAmount = parseFloat(amount.replace(',', '.'));
      
      if (isNaN(numericAmount)) {
        throw new Error('Ongeldig bedrag');
      }

      const { error } = await supabase
        .from('hidden_revenue')
        .insert([{
          user_id: user.id,
          date: format(actualDate, 'yyyy-MM-dd'),
          amount: numericAmount
        }]);

      if (error) throw error;
      
      toast.success('Omzet toegevoegd');
      setShowHiddenForm(false);
      setAmount('');
    } catch (error) {
      console.error('Error saving hidden revenue:', error);
      toast.error('Fout bij het opslaan');
    } finally {
      setSubmitting(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/total' && location.pathname === '/') {
      return true;
    }
    return location.pathname.startsWith(path);
  };

  const handleAddShiftRevenue = () => {
    const now = new Date();
    const shiftDate = now.getHours() < 7 
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1) 
      : now;
    
    const dateStr = format(shiftDate, 'yyyy-MM-dd');
    navigate(`/month/${format(shiftDate, 'yyyy/MM')}`);
    
    // Set a session storage flag to open the form when the page loads
    sessionStorage.setItem('openRevenueForm', dateStr);
  };

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 lg:hidden z-50 shadow-uber pb-safe-area-bottom translate-y-0">
      <div className="grid grid-cols-4 h-14">
        {/* Links: Dashboard */}
        <button
          onClick={() => navigate('/total')}
          className={`flex flex-col items-center justify-center h-full transition-all duration-200 ${
            isActive('/total') || location.pathname === '/'
              ? 'text-black dark:text-white font-semibold'
              : 'text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400'
          }`}
        >
          <LayoutDashboard className="h-5 w-5" />
          <span className="text-[10px] mt-0.5">Dashboard</span>
        </button>
        
        {/* Dienst Afronden */}
        <button
          onClick={handleAddShiftRevenue}
          className="flex flex-col items-center justify-center h-full text-accent transition-all duration-200"
        >
          <PlusCircle className="h-5 w-5" />
          <span className="text-[10px] mt-0.5 font-semibold">Dienst</span>
        </button>
        
        {/* Hidden Revenue */}
        <button
          onClick={() => setShowHiddenForm(true)}
          className="flex flex-col items-center justify-center h-full text-accent transition-all duration-200"
        >
          <Coins className="h-5 w-5" />
          <span className="text-[10px] mt-0.5 font-semibold">Fooi</span>
        </button>
        
        {/* Rechts: Instellingen */}
        <button
          onClick={() => navigate('/settings')}
          className={`flex flex-col items-center justify-center h-full transition-all duration-200 ${
            isActive('/settings')
              ? 'text-black dark:text-white font-semibold'
              : 'text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400'
          }`}
        >
          <Settings className="h-5 w-5" />
          <span className="text-[10px] mt-0.5">Instellingen</span>
        </button>
      </div>
      </div>

      {/* Hidden Revenue Form Modal */}
      {showHiddenForm && (
        <div className="fixed inset-0 z-[99999]">
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm" 
            onClick={() => setShowHiddenForm(false)} 
            aria-hidden="true" 
          />
          
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-800">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                      Fooi
                    </h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {format(
                        new Date().getHours() < 7
                          ? new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 1)
                          : new Date(),
                        'EEEE d MMMM yyyy',
                        { locale: nl }
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowHiddenForm(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
                      Bedrag (€) *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="decimal"
                        required
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0,00"
                        className="block w-full rounded-lg border-2 border-gray-200 dark:border-gray-700 px-3 py-2.5 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 focus:border-accent focus:bg-white dark:focus:bg-gray-800 focus:outline-none transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowHiddenForm(false)}
                      className="px-4 py-2 border-2 border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                    >
                      Annuleren
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !amount}
                      className="px-4 py-2 border border-transparent rounded-lg text-sm font-semibold text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      {submitting ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Toevoegen...
                        </div>
                      ) : (
                        'Toevoegen'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}